import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getOrgMembers } from '../services/authService';
import './Grades.css';

const Grades = () => {
  const { orgDomain, isOrgAdminUser, isTeamMember } = useAuth();
  const [members, setMembers] = useState([]);
  const [memberGrades, setMemberGrades] = useState({}); // {userId: {courses: [...], overallGPA: number}}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [expandedCourses, setExpandedCourses] = useState(new Set()); // Format: "userId_courseName"

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!orgDomain) return;

      // Load organization members
      const membersList = await getOrgMembers(orgDomain);
      setMembers(membersList);

      // Load grades for each member
      const gradesMap = {};
      
      for (const member of membersList) {
        try {
          const userRef = doc(db, 'users', member.userId);
          const coursesRef = collection(userRef, 'courses');
          const coursesSnapshot = await getDocs(coursesRef);
          
          const courses = [];
          let totalWeightedGrade = 0;
          let totalWeight = 0;
          let totalGradedCourses = 0;

          for (const courseDoc of coursesSnapshot.docs) {
            const courseName = courseDoc.id;
            const courseData = courseDoc.data();
            const gradingBreakdown = courseData.gradingBreakdown || {};
            
            // Get all assignments (both completed and pending)
            const assignmentsRef = collection(courseDoc.ref, 'assignments');
            const assignmentsSnapshot = await getDocs(assignmentsRef);
            
            const assignments = [];
            let courseGrade = null;
            let hasGradedAssignments = false;
            let hasPendingAssignments = false;
            
            // Calculate course grade using weighted average based on grading breakdown
            const categoryGrades = {}; // {category: [grades]}
            const categoryWeights = {}; // {category: weight}
            
            // Parse grading breakdown to get weights
            Object.entries(gradingBreakdown).forEach(([category, weightStr]) => {
              const weight = parseFloat(weightStr.replace('%', '')) || 0;
              categoryWeights[category] = weight;
              categoryGrades[category] = [];
            });

            assignmentsSnapshot.forEach((assignmentDoc) => {
              const assignmentData = assignmentDoc.data();
              const assignment = {
                id: assignmentDoc.id,
                assignmentName: assignmentData.assignmentName || 'Unknown',
                date: assignmentData.date?.toDate() || null,
                status: assignmentData.status || false,
                category: assignmentData.category || null,
                grade: assignmentData.grade != null ? assignmentData.grade : null,
              };
              
              assignments.push(assignment);
              
              // status: true = completed, status: false = pending
              if (!assignment.status) {
                hasPendingAssignments = true;
              }
              
              if (assignment.grade != null && assignment.category) {
                hasGradedAssignments = true;
                if (!categoryGrades[assignment.category]) {
                  categoryGrades[assignment.category] = [];
                }
                categoryGrades[assignment.category].push(assignment.grade);
              }
            });

            // Calculate weighted course grade
            if (hasGradedAssignments) {
              let weightedSum = 0;
              let totalCategoryWeight = 0;
              
              Object.entries(categoryGrades).forEach(([category, grades]) => {
                if (grades.length > 0) {
                  const categoryAverage = grades.reduce((sum, g) => sum + g, 0) / grades.length;
                  const weight = categoryWeights[category] || 0;
                  weightedSum += categoryAverage * (weight / 100);
                  totalCategoryWeight += weight;
                }
              });
              
              if (totalCategoryWeight > 0) {
                // Normalize if total weight doesn't equal 100
                courseGrade = totalCategoryWeight === 100 
                  ? weightedSum 
                  : (weightedSum / totalCategoryWeight) * 100;
              } else if (assignments.filter(a => a.grade != null).length > 0) {
                // Fallback to simple average if no grading breakdown
                const gradedAssignments = assignments.filter(a => a.grade != null);
                courseGrade = gradedAssignments.reduce((sum, a) => sum + a.grade, 0) / gradedAssignments.length;
              }
            }

            // Sort assignments by date (most recent first)
            assignments.sort((a, b) => {
              if (!a.date && !b.date) return 0;
              if (!a.date) return 1;
              if (!b.date) return -1;
              return b.date - a.date;
            });

            courses.push({
              courseName,
              gradingBreakdown,
              assignments,
              courseGrade,
              hasGradedAssignments,
              hasPendingAssignments,
              hasNoGrades: !hasGradedAssignments && assignments.length > 0,
            });

            // Add to overall GPA calculation
            if (courseGrade != null) {
              totalWeightedGrade += courseGrade;
              totalGradedCourses++;
            }
          }

          // Calculate overall GPA (simple average of all course grades)
          const overallGPA = totalGradedCourses > 0 
            ? totalWeightedGrade / totalGradedCourses 
            : null;

          gradesMap[member.userId] = {
            courses,
            overallGPA,
            totalGradedCourses,
            totalCourses: courses.length,
          };
        } catch (err) {
          console.error(`Error loading grades for ${member.email}:`, err);
          gradesMap[member.userId] = {
            courses: [],
            overallGPA: null,
            totalGradedCourses: 0,
            totalCourses: 0,
          };
        }
      }

      setMemberGrades(gradesMap);
    } catch (err) {
      console.error('Error loading grades:', err);
      setError('Failed to load grades. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orgDomain]);

  useEffect(() => {
    if (orgDomain && (isOrgAdminUser || isTeamMember)) {
      loadData();
    }
  }, [orgDomain, isOrgAdminUser, isTeamMember, loadData]);

  const toggleStudent = (userId) => {
    const newExpanded = new Set(expandedStudents);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
      // Also collapse all courses for this student
      const newExpandedCourses = new Set(expandedCourses);
      Array.from(expandedCourses).forEach(key => {
        if (key.startsWith(`${userId}_`)) {
          newExpandedCourses.delete(key);
        }
      });
      setExpandedCourses(newExpandedCourses);
    } else {
      newExpanded.add(userId);
    }
    setExpandedStudents(newExpanded);
  };

  const toggleCourse = (userId, courseName) => {
    const key = `${userId}_${courseName}`;
    const newExpanded = new Set(expandedCourses);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedCourses(newExpanded);
  };

  // Check if user is admin or team member
  if (!isOrgAdminUser && !isTeamMember) {
    return (
      <div className="grades-page">
        <div className="page-header">
          <h1>Grades</h1>
        </div>
        <div className="error-state">
          <p>You must be an admin or team member to view grades.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grades-page">
        <div className="page-header">
          <h1>Grades</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading grades...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="grades-page">
        <div className="page-header">
          <h1>Grades</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Calculate overall statistics
  const studentsWithGrades = members.filter(m => {
    const grades = memberGrades[m.userId];
    return grades && grades.overallGPA != null;
  });

  const averageGPA = studentsWithGrades.length > 0
    ? studentsWithGrades.reduce((sum, m) => {
        const grades = memberGrades[m.userId];
        return sum + (grades?.overallGPA || 0);
      }, 0) / studentsWithGrades.length
    : 0;

  const totalCourses = members.reduce((sum, m) => {
    const grades = memberGrades[m.userId];
    return sum + (grades?.totalCourses || 0);
  }, 0);

  return (
    <div className="grades-page">
      <div className="page-header">
        <h1>Student Grades</h1>
        {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
        <button onClick={loadData} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-value">{members.length}</div>
          <div className="stat-label">Total Students</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{studentsWithGrades.length}</div>
          <div className="stat-label">Students with Grades</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{averageGPA.toFixed(1)}</div>
          <div className="stat-label">Average GPA</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalCourses}</div>
          <div className="stat-label">Total Courses</div>
        </div>
      </div>

      {members.length === 0 ? (
        <div className="empty-state">
          <p>No members found.</p>
        </div>
      ) : (
        <div className="grades-table-container">
          <table className="grades-table">
            <thead>
              <tr>
                <th style={{ width: '30px' }}></th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th className="gpa-column">Overall GPA</th>
                <th>Courses</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const grades = memberGrades[member.userId] || {
                  courses: [],
                  overallGPA: null,
                  totalGradedCourses: 0,
                  totalCourses: 0,
                };
                const isExpanded = expandedStudents.has(member.userId);

                return (
                  <React.Fragment key={member.userId}>
                    <tr 
                      className="student-row"
                      onClick={() => toggleStudent(member.userId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <span className="expand-icon">
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </td>
                      <td>
                        <div className="member-cell">
                          <div className="member-avatar">
                            {member.photoURL && (
                              <img 
                                src={member.photoURL} 
                                alt={member.name}
                                className="avatar-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const fallback = e.target.nextElementSibling;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            )}
                            <span className="avatar-fallback" style={{ display: member.photoURL ? 'none' : 'flex' }}>
                              {member.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          {member.name}
                        </div>
                      </td>
                      <td>{member.email}</td>
                      <td>
                        {member.isAdmin && (
                          <span className="role-badge admin-badge">Admin</span>
                        )}
                        {member.isTeamMember && !member.isAdmin && (
                          <span className="role-badge team-badge">Team</span>
                        )}
                        {!member.isAdmin && !member.isTeamMember && (
                          <span className="role-badge member-badge">Member</span>
                        )}
                      </td>
                      <td className="gpa-column">
                        {grades.overallGPA != null ? (
                          <div className="gpa-display">
                            <span className="gpa-value">{grades.overallGPA.toFixed(1)}</span>
                            <span className="gpa-unit">%</span>
                          </div>
                        ) : (
                          <span className="no-grade">No grades</span>
                        )}
                      </td>
                      <td>
                        <span className="course-count">
                          {grades.totalCourses} {grades.totalCourses === 1 ? 'course' : 'courses'}
                          {grades.totalGradedCourses > 0 && (
                            <span className="graded-count">
                              {' '}({grades.totalGradedCourses} graded)
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan="6" className="expanded-content">
                          {grades.courses.length === 0 ? (
                            <div className="no-courses-message">
                              <p>No courses found for this student.</p>
                            </div>
                          ) : (
                            <div className="courses-list">
                              {grades.courses.map((course) => {
                                const courseKey = `${member.userId}_${course.courseName}`;
                                const isCourseExpanded = expandedCourses.has(courseKey);

                                return (
                                  <div key={course.courseName} className="course-card">
                                    <div 
                                      className="course-header"
                                      onClick={() => toggleCourse(member.userId, course.courseName)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <span className="expand-icon-small">
                                        {isCourseExpanded ? '▼' : '▶'}
                                      </span>
                                      <div className="course-info">
                                        <span className="course-name">{course.courseName}</span>
                                        {course.courseGrade != null ? (
                                          <span className="course-grade">
                                            {course.courseGrade.toFixed(1)}%
                                          </span>
                                        ) : course.hasNoGrades ? (
                                          <span className="status-badge no-grades-badge">
                                            No grades
                                          </span>
                                        ) : (
                                          <span className="status-badge pending-badge">
                                            No assignments
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {isCourseExpanded && (
                                      <div className="assignments-list">
                                        {course.assignments.length === 0 ? (
                                          <div className="no-assignments">
                                            <p>No assignments found for this course.</p>
                                          </div>
                                        ) : (
                                          <table className="assignments-table">
                                            <thead>
                                              <tr>
                                                <th>Assignment</th>
                                                <th>Date</th>
                                                <th>Category</th>
                                                <th>Status</th>
                                                <th>Grade</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {course.assignments.map((assignment) => (
                                                <tr key={assignment.id}>
                                                  <td>{assignment.assignmentName}</td>
                                                  <td>
                                                    {assignment.date
                                                      ? assignment.date.toLocaleDateString()
                                                      : 'No date'}
                                                  </td>
                                                  <td>
                                                    {assignment.category || (
                                                      <span className="no-category">—</span>
                                                    )}
                                                  </td>
                                                  <td>
                                                    {assignment.status ? (
                                                      <span className="status-badge completed-badge">
                                                        Completed
                                                      </span>
                                                    ) : (
                                                      <span className="status-badge pending-badge">
                                                        Pending
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td>
                                                    {assignment.grade != null ? (
                                                      <span className="grade-value">
                                                        {assignment.grade.toFixed(1)}%
                                                      </span>
                                                    ) : (
                                                      <span className="no-grade">—</span>
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Grades;
